/**
 * The model tree's shape and its rules, apart from anything that draws it.
 *
 * GET /api/elements answers flat rows carrying a depth, because that is what
 * the database produces cheaply; the nesting is rebuilt here. An off-by-one in
 * that rebuild silently reparents half the tree and still renders perfectly, so
 * every function in this file is pure and exported, and tested directly rather
 * than through a render.
 *
 * The refusal rules live here for the same reason. Which actions a row offers
 * depends on three unrelated facts - whether BEST projects the kind at all,
 * whether the row belongs to the shared library, and whether this person may
 * write to this branch - and getting that wrong shows up as a button that
 * promises something the API refuses. It is decided once, in one function.
 */

import type { ElementRow } from '../../schema'
import { matchesTerms, searchableText } from '../entity/rowFilter'

export interface TreeNode {
  row: ElementRow
  /**
   * Unique inside one tree: the row's position in the flat listing.
   *
   * Not the qualified name. Two elements can share one - an unnamed usage has
   * no name to qualify - and a duplicate itemId makes MUI's tree view collapse
   * the two rows into one.
   */
  key: string
  /**
   * The row this one is declared inside, walked from containment.
   *
   * The ElementRow rather than the TreeNode, so a node can be compared, copied
   * and serialised in a test without a cycle to trip over.
   */
  parent: ElementRow | null
  children: TreeNode[]
  /**
   * Every file this node is declared in, in walk order.
   *
   * One, except for a package a split program re-opens in several files
   * (issue #27): the listing answers a row per file, and the tree shows it
   * once (issue #53) with all of them here.
   */
  paths: string[]
}

/** How this person may act on the model, as useCanWrite() answers it. */
export interface WritePermission {
  allowed: boolean
  reason: string
}

/** Something BEST stores but does not project: no fields, so no form. */
export const UNPROJECTED = 'element'

export const LIBRARY_REFUSAL =
  'The shared library is not this program’s to edit. Change it in the library, not here.'

export const PACKAGE_REFUSAL =
  'BEST has no write path for a package itself yet — only for the declarations inside one.'

/**
 * Rebuild nesting from a flat list carrying depth, one package per name.
 *
 * A row deeper than its predecessor by more than one still lands somewhere
 * defensible rather than throwing or being dropped: the tree disagreeing with
 * the file by one level of indentation is a nuisance, and losing an element
 * altogether is the bug that makes people stop trusting the screen.
 *
 * The listing walks file by file, so a package re-opened in several files
 * arrives as several roots. Those are folded into one - see mergeReopened.
 */
export function toTree(rows: ElementRow[]): TreeNode[] {
  return mergeReopened(nest(rows))
}

function nest(rows: ElementRow[]): TreeNode[] {
  const roots: TreeNode[] = []
  const stack: TreeNode[] = []

  rows.forEach((row, index) => {
    // Drop back to this row's parent level before reading the parent off the
    // stack, so the stack's height is always the depth about to be filled.
    while (stack.length > row.depth) stack.pop()
    const parent = stack.length ? stack[stack.length - 1] : null
    const node: TreeNode = {
      row,
      key: String(index),
      parent: parent ? parent.row : null,
      children: [],
      paths: [row.path],
    }
    if (parent) parent.children.push(node)
    else roots.push(node)
    stack.push(node)
  })

  return roots
}

/**
 * Fold sibling packages that are the same package into one node.
 *
 * A split program declares 'package EXAMPLE { package Requirements {' in every
 * file under it (issue #27), so the listing has an EXAMPLE per file. That is
 * one package - the metamodel and the package picker (#45) already say so -
 * and a tree showing it once per file is issue #53.
 *
 * Only PACKAGES, and only siblings with the same qualified name and the same
 * library standing. Two usages can share a qualified name without being one
 * thing (an unnamed usage has no name to qualify), and folding those would
 * lose a row. The first declaration's row and key stand for the package;
 * children are concatenated in walk order and folded again one level down,
 * which is where the re-opened sub-packages now meet.
 */
export function mergeReopened(nodes: TreeNode[]): TreeNode[] {
  const merged: TreeNode[] = []
  const packages = new Map<string, TreeNode>()

  for (const node of nodes) {
    const { row } = node
    const name = row.kind === 'package' && row.qualified_name
      ? `${row.is_library ? 'library' : 'program'}:${row.qualified_name}`
      : ''
    const kept = name ? packages.get(name) : undefined
    if (!kept) {
      const copy = { ...node, children: [...node.children], paths: [...node.paths] }
      if (name) packages.set(name, copy)
      merged.push(copy)
      continue
    }
    kept.children.push(...node.children)
    for (const path of node.paths) {
      if (!kept.paths.includes(path)) kept.paths.push(path)
    }
  }

  return merged.map((node) => ({
    ...node,
    // Re-pointed at the row that stands for the package, so nothing below can
    // reach a declaration the tree no longer shows.
    children: mergeReopened(node.children).map((child) => ({ ...child, parent: node.row })),
  }))
}

/**
 * Everything about a row somebody might reasonably type to look for it.
 *
 * Through the shared matcher rather than a hand-listed field set: this used to
 * test id/name/qualified_name/type_name/kind, which meant a row that gained a
 * field showed it in the tree and could not be found by it. Same rule as every
 * other filter in the app, including 'every term must hold'.
 */
function matches(row: ElementRow, wanted: string): boolean {
  return matchesTerms(searchableText(row as unknown as Record<string, unknown>), wanted)
}

/**
 * Narrow the tree to matches, keeping the ancestors of each one.
 *
 * A match is shown in context: its ancestors are kept so it can be seen where
 * it sits, and - unlike the flowdown screen's filter - so is its subtree,
 * because a package whose name matches would otherwise be drawn as an empty
 * container and read as "nothing in there matches" rather than "everything in
 * here does".
 */
export function prune(nodes: TreeNode[], text: string): TreeNode[] {
  const wanted = text.trim().toLowerCase()
  if (!wanted) return nodes

  const keep = (node: TreeNode): TreeNode | null => {
    if (matches(node.row, wanted)) return node
    const children = node.children
      .map(keep)
      .filter((child): child is TreeNode => child !== null)
    return children.length ? { ...node, children } : null
  }

  return nodes.map(keep).filter((node): node is TreeNode => node !== null)
}

/** Every key in a tree, for opening it all at once behind a filter. */
export function allKeys(nodes: TreeNode[]): string[] {
  return nodes.flatMap((node) => [node.key, ...allKeys(node.children)])
}

/**
 * The keys to open on first sight: packages, and nothing else.
 *
 * The packaging is the map, so it is open; the declarations inside are the
 * detail, and opening every part's ports at once buries it.
 */
export function packageKeys(nodes: TreeNode[]): string[] {
  return nodes.flatMap((node) => [
    ...(node.row.kind === 'package' ? [node.key] : []),
    ...packageKeys(node.children),
  ])
}

/** How many rows a tree holds, for saying so. */
export function countNodes(nodes: TreeNode[]): number {
  return nodes.reduce((total, node) => total + 1 + countNodes(node.children), 0)
}

/**
 * The package a new declaration would go in, or '' when there is none.
 *
 * 'child' puts it inside the node, 'sibling' beside it - which is what
 * duplicating means. Empty is a real answer and the caller must refuse on it:
 * POST /api/{plural} takes a package and nothing else, so an element declared
 * inside a part has nowhere for BEST to state a sibling.
 *
 * Read off containment rather than assembled by trimming the qualified name,
 * because the parent's own qualified name is the only spelling guaranteed to
 * resolve.
 */
export function destinationPackage(node: TreeNode, mode: 'child' | 'sibling'): string {
  if (mode === 'child') {
    return node.row.kind === 'package' ? node.row.qualified_name : ''
  }
  return node.parent?.kind === 'package' ? node.parent.qualified_name : ''
}

export type TreeAction =
  | 'reveal'
  | 'source'
  | 'rename'
  | 'duplicate'
  | 'delete'
  | 'new'

/**
 * Why an action is unavailable on a row, or '' when it is available.
 *
 * Every caller DISABLES on a non-empty answer and shows it as the tooltip; no
 * caller hides the control. A missing button is indistinguishable from a broken
 * page, and three of the four refusals here are things a person can act on -
 * sign in, leave the default branch, edit the library where it lives.
 */
export function refusalFor(
  action: TreeAction,
  node: TreeNode,
  write: WritePermission,
): string {
  const { row } = node
  const article = row.kind.match(/^[aeiou]/i) ? 'an' : 'a'

  // Reading is never refused on permission - both of these are read-only, and
  // the library is exactly what somebody most wants to read the source of.
  if (action === 'source') {
    return row.qualified_name ? '' : 'This element has no qualified name to export a subtree by.'
  }
  if (action === 'reveal') {
    if (!row.plural) {
      return `BEST does not project ${article} ${row.kind}, so it has no page of its own.`
    }
    return row.id ? '' : 'This element has no ID for a page to be addressed by.'
  }

  // The library first: it is permanent and specific, where the branch and the
  // role are both things this person could change in a minute.
  if (row.is_library) return LIBRARY_REFUSAL
  if (!write.allowed) return write.reason

  // Packages only, for both of New's routes. A wizard states one declaration
  // through the registry, which knows where each kind may live, but POST
  // /api/{plural} takes a package; and a paste is whatever somebody typed, so
  // whether it may sit inside anything else is a question about what was
  // pasted, which neither end reads closely enough to answer.
  if (action === 'new') {
    return row.kind === 'package'
      ? ''
      : 'Something new can only be stated inside a package.'
  }

  if (row.kind === 'package') return PACKAGE_REFUSAL
  if (!row.plural) {
    return `BEST does not project ${article} ${row.kind}, so there is no form to edit it with.`
  }
  if (!row.id) return 'This element has no ID for the API to address it by.'

  if (action === 'duplicate' && !destinationPackage(node, 'sibling')) {
    const inside = node.parent ? ` inside ${node.parent.name || node.parent.qualified_name}` : ''
    return `It is declared${inside}, not in a package, and BEST can only state a new declaration in a package.`
  }

  return ''
}
